/**
 * `LiveContextRow` — page-local presentation helper for one rule-context
 * variable in the admin `ComponentConfigPage`.
 *
 * Renders the existing click-to-insert variable badge on the left and a
 * monospace "live value" chip on the right. Between them, when the
 * descriptor carries a `sourceField`, a muted `→ FIELD` annotation surfaces
 * the underlying VEOS/ERP field the variable derives from — visibly, so
 * rule authors see the origin at a glance rather than only on badge hover.
 * The right side shows
 *
 * - the captured value formatted per {@link formatLiveValue}, with a
 *   `Tooltip` exposing the full string when truncated;
 * - or a muted em-dash placeholder when the variable was not present in
 *   the last captured context bag.
 *
 * Not a design-system component — intentionally page-local so the catalog
 * row stays readable.
 */

import { Button } from '@components/base/actions';
import { Badge, Text, Tooltip } from '@components/base/display';
import { HStack } from '@components/base/layout';

import type { ContextVariableDescriptor } from '@components/shared/configurable';

const MAX_LIVE_VALUE_LENGTH = 80;

export interface LiveContextRowProps {
  readonly name: string;
  readonly descriptor: ContextVariableDescriptor;
  readonly liveValue: unknown;
  readonly hasCapture: boolean;
  readonly onInsert?: (variableName: string) => void;
}

interface FormattedValue {
  readonly text: string;
  readonly truncated: boolean;
  readonly muted: boolean;
  readonly fullText: string;
}

/**
 * Format a captured value for display in the chip.
 *
 * - `null` / `undefined` → muted em-dash placeholder.
 * - `string` → quoted, truncated to {@link MAX_LIVE_VALUE_LENGTH} chars.
 * - `number` / `boolean` → `String(v)`.
 * - arrays / objects → `JSON.stringify(v)`, truncated.
 */
function formatLiveValue(value: unknown): FormattedValue {
  if (value === null || value === undefined) {
    return { text: '—', fullText: '—', truncated: false, muted: true };
  }
  let raw: string;
  if (typeof value === 'string') {
    raw = `"${value}"`;
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    raw = String(value);
  } else {
    try {
      raw = JSON.stringify(value);
    } catch {
      raw = String(value);
    }
  }
  if (raw.length > MAX_LIVE_VALUE_LENGTH) {
    return {
      text: `${raw.slice(0, MAX_LIVE_VALUE_LENGTH)}…`,
      fullText: raw,
      truncated: true,
      muted: false,
    };
  }
  return { text: raw, fullText: raw, truncated: false, muted: false };
}

export function LiveContextRow({
  name,
  descriptor,
  liveValue,
  hasCapture,
  onInsert,
}: Readonly<LiveContextRowProps>) {
  const label = `${name}: ${descriptor.type}`;
  const badge = (
    <Badge variant="info" size="sm" pill>
      {label}
    </Badge>
  );
  const control =
    onInsert !== undefined ? (
      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => { e.preventDefault(); }}
        onClick={() => { onInsert(name); }}
        aria-label={`Insert ${name} variable`}
      >
        {badge}
      </Button>
    ) : (
      <span>{badge}</span>
    );
  const wrappedControl =
    descriptor.description !== undefined && descriptor.description !== '' ? (
      <Tooltip content={descriptor.description}>{control}</Tooltip>
    ) : (
      control
    );

  // When no capture exists at all, show the same em-dash placeholder so
  // the column lines up. The caption above the list explains why.
  const showAsMissing = !hasCapture || liveValue === undefined;
  const formatted = showAsMissing
    ? { text: '—', fullText: '—', truncated: false, muted: true }
    : formatLiveValue(liveValue);

  const valueText = (
    <Text
      size="sm"
      color={formatted.muted ? 'muted' : 'default'}
      className="font-mono"
    >
      {formatted.text}
    </Text>
  );

  return (
    <HStack gap={2} align="center">
      {wrappedControl}
      {/* Surface the origin VEOS/ERP field visibly (not tooltip-gated) so
          rule authors can see where the variable comes from at a glance. */}
      {descriptor.sourceField !== undefined && descriptor.sourceField !== '' ? (
        <Text size="sm" color="muted" className="font-mono">
          → {descriptor.sourceField}
        </Text>
      ) : null}
      {formatted.truncated ? (
        <Tooltip content={formatted.fullText}>
          <span>{valueText}</span>
        </Tooltip>
      ) : (
        valueText
      )}
    </HStack>
  );
}
